/**
 * Quiz feedback SFX (solo + PvP). Files live under `public/assets/sounds/`.
 * Pool + preload so playback starts without waiting on network.
 * WITC typewriter and Tile Match picks use small Audio pools preloaded the same way.
 * Playback is skipped when the user has muted quiz sounds (see `getQuizSfxEnabled`).
 */

const sfxBase = `${process.env.PUBLIC_URL || ''}/assets/sounds`;

const STORAGE_KEY = 'quizterQuizSoundsEnabled';

let correctAudio: HTMLAudioElement | null = null;
let incorrectAudio: HTMLAudioElement | null = null;
let rewardAudio: HTMLAudioElement | null = null;
let celebrationTadaAudio: HTMLAudioElement | null = null;

/** WITC answer field: short clip per key; pool avoids clipping when typing fast. */
const WITC_TYPEWRITER_POOL_SIZE = 8;
let witcTypewriterPool: HTMLAudioElement[] = [];
let witcTypewriterPoolIndex = 0;

/** Tile Match tile pick: `plink-sfx.mp3`; pool for rapid taps. */
const TILE_MATCH_PLINK_POOL_SIZE = 8;
let tileMatchPlinkPool: HTMLAudioElement[] = [];
let tileMatchPlinkPoolIndex = 0;

/** When false, quiz SFX (correct, incorrect, reward, WITC typewriter, Tile Match plink) are not played. */
let sfxEnabled = true;

function readStoredPreference(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      sfxEnabled = true;
      return;
    }
    sfxEnabled = raw === 'true' || raw === '1';
  } catch {
    sfxEnabled = true;
  }
}

/**
 * Call once on app load before first SFX. Reads cached preference from localStorage (default: sounds on).
 */
export function initQuizSfxPreference(): void {
  readStoredPreference();
}

/**
 * Apply preference from GET /user/profile (authoritative when logged in).
 */
export function applyQuizSfxPreferenceFromServer(enabled: boolean): void {
  sfxEnabled = enabled;
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
  } catch {
    /* ignore */
  }
}

export function getQuizSfxEnabled(): boolean {
  return sfxEnabled;
}

/** Updates runtime + localStorage (no API). */
export function setQuizSfxEnabledLocal(enabled: boolean): void {
  sfxEnabled = enabled;
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
  } catch {
    /* ignore */
  }
}

function createSfx(url: string): HTMLAudioElement {
  const a = new Audio(url);
  a.preload = 'auto';
  return a;
}

function preloadSfxPool(url: string, poolSize: number, existing: HTMLAudioElement[]): HTMLAudioElement[] {
  if (existing.length > 0) return existing;
  const pool = Array.from({ length: poolSize }, () => createSfx(url));
  for (const el of pool) {
    void el.load();
  }
  return pool;
}

function preloadWitcTypewriterSfx(): void {
  if (typeof window === 'undefined') return;
  witcTypewriterPool = preloadSfxPool(
    `${sfxBase}/typewriter-sfx.mp3`,
    WITC_TYPEWRITER_POOL_SIZE,
    witcTypewriterPool
  );
}

function preloadTileMatchPlinkSfx(): void {
  if (typeof window === 'undefined') return;
  tileMatchPlinkPool = preloadSfxPool(
    `${sfxBase}/plink-sfx.mp3`,
    TILE_MATCH_PLINK_POOL_SIZE,
    tileMatchPlinkPool
  );
}

function playSfxPoolClip(pool: HTMLAudioElement[], poolIndex: number): void {
  if (!pool.length) return;
  const el = pool[poolIndex % pool.length]!;
  try {
    el.pause();
    el.currentTime = 0;
    void el.play().catch(() => {
      /* autoplay policy or missing file */
    });
  } catch {
    /* ignore */
  }
}

/**
 * Idempotent. Call once early (e.g. App mount) so files are decoded before first play.
 */
export function preloadQuizSfx(): void {
  if (typeof window === 'undefined') return;
  preloadWitcTypewriterSfx();
  preloadTileMatchPlinkSfx();
  if (correctAudio) return;
  correctAudio = createSfx(`${sfxBase}/correct-sfx.mp3`);
  incorrectAudio = createSfx(`${sfxBase}/incorrect-sfx.mp3`);
  rewardAudio = createSfx(`${sfxBase}/reward-sfx.mp3`);
  celebrationTadaAudio = createSfx(`${sfxBase}/tada-sfx.mp3`);
  for (const el of [correctAudio, incorrectAudio, rewardAudio, celebrationTadaAudio]) {
    void el.load();
  }
}

function ensurePool(): void {
  if (!correctAudio) preloadQuizSfx();
}

function playFromPool(el: HTMLAudioElement | null): void {
  if (!el) return;
  try {
    el.pause();
    el.currentTime = 0;
    void el.play().catch(() => {
      /* autoplay policy or missing file */
    });
  } catch {
    /* ignore */
  }
}

export function playQuizSfxCorrect(): void {
  if (!sfxEnabled) return;
  ensurePool();
  playFromPool(correctAudio);
}

export function playQuizSfxIncorrect(): void {
  if (!sfxEnabled) return;
  ensurePool();
  playFromPool(incorrectAudio ?? null);
}

export function playQuizSfxReward(): void {
  if (!sfxEnabled) return;
  ensurePool();
  playFromPool(rewardAudio ?? null);
}

/**
 * WITC text field: play when the user inserts a character (not on delete).
 * Respects the same mute flag as other quiz SFX. Preload via {@link preloadQuizSfx}.
 */
export function playWitcTypewriterKeystrokeSfx(): void {
  if (!sfxEnabled) return;
  preloadWitcTypewriterSfx();
  const idx = witcTypewriterPoolIndex;
  witcTypewriterPoolIndex += 1;
  playSfxPoolClip(witcTypewriterPool, idx);
}

/**
 * Tile Match: play when the user picks a tile (same mute flag / pool pattern as WITC typewriter).
 * File: `public/assets/sounds/plink-sfx.mp3`.
 */
export function playTileMatchPickSfx(): void {
  if (!sfxEnabled) return;
  preloadTileMatchPlinkSfx();
  const idx = tileMatchPlinkPoolIndex;
  tileMatchPlinkPoolIndex += 1;
  playSfxPoolClip(tileMatchPlinkPool, idx);
}

/**
 * Short sting when {@link QuizFinishCelebrationModal} opens (solo + PvP). Same mute flag as quiz SFX.
 * File: `public/assets/sounds/tada-sfx.mp3`.
 */
export function playCelebrationTadaSfx(): void {
  if (!sfxEnabled) return;
  ensurePool();
  playFromPool(celebrationTadaAudio);
}
